import { getApp } from 'firebase/app'
import {
    getAI,
    getGenerativeModel,
    VertexAIBackend,
    Schema,
    type GenerateContentResult,
} from 'firebase/ai'
import type { Part } from 'firebase/ai'
import { type Functions, httpsCallable } from 'firebase/functions'
import type { LLMServiceInterface, LLMRequestParams } from './types' // Assume LLMResponseJSONSchema exported
import type {
    LLMResponseJSONSchema,
    LLMRequestContent,
} from '~common/llms/types'
import type {
    GetLLMReplyInput,
    GetLLMReplyOutput,
} from '~common/llms/functions/types'

// Partial config for known fields
type GenerationConfigPartial = {
    temperature?: number
    responseMimeType?: string
    responseSchema?: ReturnType<typeof Schema.object>
}

export interface FirebaseLLMServiceDeps {
    functions: Functions
}

export class FirebaseLLMService implements LLMServiceInterface {
    constructor(private readonly deps: FirebaseLLMServiceDeps) {}

    private requestQueue: Array<{
        task: () => Promise<unknown>
        resolve: (value: unknown) => void
        reject: (reason?: unknown) => void
    }> = []

    private processing = false
    private lastCall = 0
    private readonly interval = 60000 / 90 // ms for 90 requests per minute

    private enqueueVertexRequest<T>(task: () => Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            this.requestQueue.push({
                task: task as () => Promise<unknown>,
                resolve: resolve as (value: unknown) => void,
                reject: reject as (reason?: unknown) => void,
            })
            if (!this.processing) {
                this.processNext()
            }
        })
    }

    private async processNext(): Promise<void> {
        if (this.processing) return
        this.processing = true

        try {
            while (this.requestQueue.length > 0) {
                const item = this.requestQueue.shift()!
                try {
                    const now = Date.now()
                    const wait = Math.max(
                        0,
                        this.lastCall + this.interval - now,
                    )
                    if (wait > 0) {
                        await new Promise((r) => setTimeout(r, wait))
                    }
                    const result = await item.task()
                    this.lastCall = Date.now()
                    ;(item.resolve as (value: unknown) => void)(result)
                } catch (e) {
                    item.reject(e)
                }
            }
        } finally {
            this.processing = false
            if (this.requestQueue.length > 0) {
                this.processNext()
            }
        }
    }

    private buildSchemaFromJSON(
        schema: LLMResponseJSONSchema['schema'],
    ): ReturnType<typeof Schema.object> {
        return Schema.object({
            properties: this.buildProperties(schema.properties),
            required: schema.required,
            additionalProperties: schema.additionalProperties,
            nullable: false,
        })
    }

    private buildItemSchema(item: unknown): Schema {
        if (typeof item === 'object' && item !== null && 'type' in item) {
            const typedItem = item as {
                type: string
                properties?: Record<string, unknown>
                description?: string
                pattern?: string
            }
            if (typedItem.type === 'object') {
                return this.buildSchemaFromJSON(
                    typedItem as LLMResponseJSONSchema['schema'],
                )
            } else {
                return this.buildSimpleProperty(typedItem)
            }
        }
        return Schema.string({ nullable: false }) // Fallback
    }

    private buildProperties(
        props: Record<string, unknown>,
    ): Record<
        string,
        ReturnType<
            | typeof Schema.string
            | typeof Schema.number
            | typeof Schema.boolean
            | typeof Schema.object
            | typeof Schema.array
        >
    > {
        const built: Record<string, unknown> = {}
        for (const [key, prop] of Object.entries(props)) {
            if (typeof prop === 'object' && prop !== null && 'type' in prop) {
                const typedProp = prop as {
                    type: string
                    items?: unknown
                    properties?: Record<string, unknown>
                }
                if (typedProp.type === 'array') {
                    built[key] = Schema.array({
                        items: this.buildItemSchema(typedProp.items),
                        nullable: false,
                    })
                } else if (typedProp.type === 'object') {
                    built[key] = this.buildSchemaFromJSON(
                        typedProp.properties as unknown as LLMResponseJSONSchema['schema'],
                    ) // Recursive for nested objects
                } else {
                    built[key] = this.buildSimpleProperty(typedProp)
                }
            } else {
                built[key] = Schema.string({ nullable: false }) // Fallback for invalid props
            }
        }
        return built as Record<
            string,
            Schema &
                ReturnType<
                    | typeof Schema.string
                    | typeof Schema.number
                    | typeof Schema.boolean
                    | typeof Schema.object
                    | typeof Schema.array
                >
        >
    }

    private buildSimpleProperty(prop: {
        type: string
        description?: string
        pattern?: string
    }): ReturnType<
        typeof Schema.string | typeof Schema.number | typeof Schema.boolean
    > {
        // Remove the Array.isArray check or handle similarly with type guard
        switch (prop.type) {
            case 'string':
                return Schema.string({
                    description: prop.description,
                    pattern: prop.pattern,
                    nullable: false,
                })
            case 'number':
                return Schema.number({
                    description: prop.description,
                    nullable: false,
                })
            case 'boolean':
                return Schema.boolean({
                    description: prop.description,
                    nullable: false,
                })
            default:
                return Schema.string({ nullable: false }) // Fallback
        }
    }

    private async getReplyClientside<T = unknown>(
        params: LLMRequestParams,
    ): Promise<T | AsyncIterable<string>> {
        const app = getApp()
        const ai = getAI(app, { backend: new VertexAIBackend() })

        const isGeminiModel = params.model.startsWith('gemini-')
        const generationConfig: GenerationConfigPartial = {}
        if (params.temperature !== undefined) {
            generationConfig.temperature = params.temperature
        }
        if (
            params.jsonResponseSchema &&
            isGeminiModel &&
            !(params.stream ?? false)
        ) {
            generationConfig.responseMimeType = 'application/json'
            generationConfig.responseSchema = this.buildSchemaFromJSON(
                params.jsonResponseSchema.schema,
            )
            // Ignore custom name/strict; use prompt for enforcement
        } else if ((params.stream ?? false) && params.jsonResponseSchema) {
            throw new Error(
                'JSON response schema is not supported in streaming mode',
            )
        }
        // For non-Gemini (e.g., Llama), rely on prompt for JSON; no responseMimeType/responseSchema

        const model = getGenerativeModel(ai, {
            model: params.model,
            generationConfig,
        })

        let messages = params.messages

        // Extract and prepend system instruction to first user message (for compatibility; skip field for non-Gemini)
        let systemInstruction: string | undefined
        if (messages.length > 0 && messages[0].role === 'system') {
            if (typeof messages[0].content === 'string') {
                systemInstruction = messages[0].content
            } else {
                // Filter to text-only parts (system instructions are text-only; skip non-text)
                const textParts = messages[0].content.filter(
                    (c): c is { type: 'text'; text: string } =>
                        c.type === 'text',
                )
                systemInstruction = textParts.map((c) => c.text).join(' ') || ''
            }
            // Prepend to first non-system message if it's user
            messages = messages.slice(1)
            if (
                messages.length > 0 &&
                messages[0].role === 'user' &&
                systemInstruction
            ) {
                const firstUserContent: string | LLMRequestContent[] =
                    typeof messages[0].content === 'string'
                        ? systemInstruction + '\n\n' + messages[0].content
                        : [
                              {
                                  type: 'text',
                                  text: systemInstruction,
                              } as LLMRequestContent,
                              ...(messages[0].content as LLMRequestContent[]),
                          ]
                messages[0] = { ...messages[0], content: firstUserContent }
            }
        }

        let historyMessages = messages

        // Build history from user/assistant pairs (slice to exclude last input)
        const history: Array<{ role: 'user' | 'model'; parts: Part[] }> = []
        const chatMessages = historyMessages.slice(0, -1)
        for (const msg of chatMessages) {
            if (msg.role === 'user' || msg.role === 'assistant') {
                const role = msg.role === 'user' ? 'user' : 'model'
                let parts: Part[]
                if (typeof msg.content === 'string') {
                    parts = [{ text: msg.content }]
                } else {
                    // Handle LLMRequestContent[] - for text and file
                    parts = msg.content.map((c): Part => {
                        if (c.type === 'text') {
                            return { text: c.text }
                        } else if (c.type === 'file') {
                            const { file } = c
                            if (!file || typeof file.file_data !== 'string') {
                                throw new Error(
                                    'Invalid file content: missing or invalid file_data',
                                )
                            }
                            // Parse data URL: data:MIME;base64,BASE64
                            const matches = file.file_data.match(
                                /^data:([^;]+);base64,(.+)$/,
                            )
                            if (!matches) {
                                throw new Error(
                                    'Invalid file_data format: expected data URL with base64',
                                )
                            }
                            const [, mimeType, base64Data] = matches
                            return {
                                inlineData: {
                                    mimeType,
                                    data: base64Data, // Base64 without data URL prefix
                                },
                            }
                        }
                        // TODO: Handle images/files by fetching base64 (other types like 'image_url')
                        throw new Error(`Unsupported content type: ${c.type}`)
                    })
                }
                history.push({ role, parts })
            }
            // Skip other roles (tool, etc.) or log warning
        }

        // Ensure history starts with 'user' if non-empty
        if (history.length > 0 && history[0].role !== 'user') {
            throw new Error('Chat history must start with a user message')
        }

        const chatOptions: {
            history?: Array<{ role: 'user' | 'model'; parts: Part[] }>
            systemInstruction?: string
        } = { history }
        // Only set systemInstruction for Gemini (Llama may not support it as Content)
        if (systemInstruction && isGeminiModel) {
            chatOptions.systemInstruction = systemInstruction
        }

        const chat = model.startChat(chatOptions)

        // Prepare last message as user input (assume it's 'user' role)
        const lastMsg = historyMessages[historyMessages.length - 1]
        if (lastMsg.role !== 'user') {
            throw new Error('Last message must be from user for sendMessage')
        }
        let userInput: string | (string | Part)[]
        if (typeof lastMsg.content === 'string') {
            userInput = lastMsg.content
        } else {
            // Handle LLMRequestContent[] - for text and file
            userInput = lastMsg.content.map((c): Part => {
                if (c.type === 'text') {
                    return { text: c.text }
                } else if (c.type === 'file') {
                    const { file } = c
                    if (!file || typeof file.file_data !== 'string') {
                        throw new Error(
                            'Invalid file content: missing or invalid file_data',
                        )
                    }
                    // Parse data URL: data:MIME;base64,BASE64
                    const matches = file.file_data.match(
                        /^data:([^;]+);base64,(.+)$/,
                    )
                    if (!matches) {
                        throw new Error(
                            'Invalid file_data format: expected data URL with base64',
                        )
                    }
                    const [, mimeType, base64Data] = matches
                    return {
                        inlineData: {
                            mimeType,
                            data: base64Data, // Base64 without data URL prefix
                        },
                    }
                }
                // TODO: Handle images/files
                throw new Error(
                    `Unsupported content type in user input: ${c.type}`,
                )
            })
        }

        if (params.stream ?? false) {
            // Use streaming
            const stream = (await this.enqueueVertexRequest(() =>
                chat.sendMessageStream(userInput),
            )) as unknown as AsyncIterable<GenerateContentResult>
            return (async function* () {
                for await (const chunk of stream) {
                    const text = chunk.response.text()
                    if (text) {
                        yield text
                    }
                }
            })() as AsyncIterable<string>
        } else {
            const result = await this.enqueueVertexRequest(() =>
                chat.sendMessage(userInput),
            )

            const text = result.response.text()
            if (params.jsonResponseSchema) {
                return JSON.parse(text) as T
            } else {
                return text as T
            }
        }
    }

    private async getReplyCloud<T = unknown>(
        params: LLMRequestParams,
    ): Promise<T | AsyncIterable<string>> {
        const getLLMReply = httpsCallable<GetLLMReplyInput, GetLLMReplyOutput>(
            this.deps.functions,
            'getLLMReply',
        )

        const result = await getLLMReply({
            model: params.model,
            messages: params.messages,
            jsonResponseSchema: params.jsonResponseSchema,
            stream: params.stream ?? false,
            onlyGoogleVertex: params.onlyGoogleVertex,
            expectedResponse: params.expectedResponse,
            reasoningEffort: params.reasoningEffort,
        })

        if (params.stream ?? false) {
            return result.data as AsyncIterable<string>
        } else {
            return result.data as T
        }
    }

    getReply: LLMServiceInterface['getReply'] = async <T = unknown>(
        params: LLMRequestParams,
    ): Promise<T | AsyncIterable<string>> => {
        if (params.clientside === true) {
            return this.getReplyClientside<T>(params)
        }
        return this.getReplyCloud<T>(params)
    }
}
